import { animate, useMotionValue, useReducedMotion } from "framer-motion";
import { useRef, type PointerEvent, type MouseEvent } from "react";

const INTERACTIVE =
  'button, a, input, textarea, select, label, [role="slider"], [role="button"], [contenteditable]:not([contenteditable="false"]), [data-no-drawer-swipe]';
type Gesture = {
  id: number;
  x: number;
  y: number;
  time: number;
  width: number;
  dragging: boolean;
};

/** Touch-only, rightward dismissal. Leave native vertical scrolling and browser
 * edge navigation alone. Feed x to the same motion element as the exit animation
 * so dismissal continues from the finger position rather than jumping to zero. */
export function useMobileDrawerSwipe(onDismiss: () => void, disabled = false) {
  const x = useMotionValue(0);
  const reducedMotion = useReducedMotion();
  const gesture = useRef<Gesture | null>(null);
  const suppressClick = useRef(false);

  const reset = () => {
    gesture.current = null;
    animate(x, 0, {
      duration: reducedMotion ? 0 : 0.2,
      ease: [0.2, 0, 0.13, 1],
    });
  };

  return {
    x,
    onPointerDown(event: PointerEvent<HTMLDivElement>) {
      if (event.isPrimary === false) {
        reset();
        return;
      }
      suppressClick.current = false;
      if (
        disabled ||
        event.pointerType !== "touch" ||
        window.innerWidth >= 768 ||
        event.clientX < 24 ||
        (event.target instanceof Element && event.target.closest(INTERACTIVE))
      )
        return;
      gesture.current = {
        id: event.pointerId,
        x: event.clientX,
        y: event.clientY,
        time: event.timeStamp,
        width: event.currentTarget.getBoundingClientRect().width,
        dragging: false,
      };
    },
    onPointerMove(event: PointerEvent<HTMLDivElement>) {
      const start = gesture.current;
      if (!start || start.id !== event.pointerId) return;
      if (disabled) {
        reset();
        return;
      }
      const dx = event.clientX - start.x;
      const dy = event.clientY - start.y;
      if (!start.dragging) {
        if (
          (Math.abs(dy) > 10 && Math.abs(dy) * 1.5 > Math.abs(dx)) ||
          dx < -10
        ) {
          gesture.current = null;
          return;
        }
        if (dx < 12 || dx < Math.abs(dy) * 1.5) return;
        start.dragging = true;
        x.stop();
        event.currentTarget.setPointerCapture(event.pointerId);
      }
      if (event.cancelable) event.preventDefault();
      suppressClick.current = true;
      x.set(Math.max(0, dx));
    },
    onPointerUp(event: PointerEvent<HTMLDivElement>) {
      const start = gesture.current;
      if (!start || start.id !== event.pointerId) return;
      gesture.current = null;
      if (!start.dragging) return;
      const dx = event.clientX - start.x;
      const elapsed = Math.max(1, event.timeStamp - start.time);
      const farEnough = dx >= Math.min(120, start.width * 0.3);
      const quickFlick = dx >= 40 && elapsed <= 150 && dx / elapsed >= 0.7;
      if (!disabled && (farEnough || quickFlick)) onDismiss();
      else reset();
    },
    onPointerCancel() {
      reset();
    },
    onLostPointerCapture() {
      if (gesture.current) reset();
    },
    onClickCapture(event: MouseEvent<HTMLDivElement>) {
      if (!suppressClick.current) return;
      suppressClick.current = false;
      event.preventDefault();
      event.stopPropagation();
    },
  };
}
